import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { invoiceApi } from '#services/invoice';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface InvoiceListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
  vendors: Record<string, unknown>;
}

interface GetInvoicesPayload {
  page: number;
  rows: unknown[];
  total: number;
  vendors: Record<string, unknown>;
}

const initialState: InvoiceListState = {
  filter: '',
  dateRange: buildTimeRange(DEFAULT_TIME_RANGE_PRESET),
  filterKey: 'invoiceNumber',
  order: 'asc',
  orderBy: 'name',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
  vendors: {},
};

const handleGetInvoicesFulfilled = (
  state: InvoiceListState,
  action: PayloadAction<unknown>,
) => {
  const {
    page,
    rows,
    total,
    vendors,
  } = action.payload as GetInvoicesPayload;
  state.pageIndex = page;
  state.rows = rows;
  state.rowCount = total;
  state.vendors = vendors;
};

export const invoiceListSlice = createSlice({
  name: 'invoiceList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(invoiceApi.endpoints.getInvoices.matchFulfilled, handleGetInvoicesFulfilled);
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
} = invoiceListSlice.actions;

export default invoiceListSlice.reducer;
